import { translateWithI18Next } from '@kfc-global/kfc-i18n/lib';
import { ButtonComp } from 'atoms/Buttons';
import ImageComponent from 'atoms/Image';
import { TRANSLATE_MAPPING_KEY } from 'common/constants/i18nTranslateKeys';
import { isDeliveryOrderCancelledOrRefund, isPickupOrderCancelled } from 'common/utilities/orderStatusUtils';
import {
  PostOrderSignInClick,
  SignUpClick,
} from 'organisms/MenuPDPPage/AU/Presentation/ReusableComponents/PdpAnalytics';
import { SESSION_STORAGE_KEYS } from 'common/constants/SharedConstants';
import { calculatePrice } from 'common/utilities/utils';
import { createAccount } from 'common/utilities/LoginUtils';

/*********************************************************************
 * Get Save your orders and checkout faster panel (Save As Favourite order)
 * params -
 *********************************************************************/
const getSaveYourOrdersAndCheckoutFasterJSX = params => {
  const {
    orderDetails,
    updateSetOrderDetails,
    saveOrderDescriptionText = TRANSLATE_MAPPING_KEY?.SAVE_YOUR_ORDERS_AND_CHECKOUT_FASTER,
    saveOrderButtonLabel = TRANSLATE_MAPPING_KEY?.SAVE_THIS_ORDER,
  } = params;
  const favOrderExist = orderDetails?.favouriteOrders?.find(order => {
    return order.id === orderDetails?.orderId;
  });

  return (
    favOrderExist === undefined && (
      <div className='savingdiv'>
        <span className='saving'>{translateWithI18Next(saveOrderDescriptionText)?.toUpperCase()}</span>
        <span className='saveButton'>
          <ButtonComp
            data-testid='save-logged-order'
            className='button transparent-btn black-border'
            onClick={() =>
              updateSetOrderDetails({
                isOpenOrderSummarySaveYourOrderCheckoutFaster:
                  !orderDetails?.isOpenOrderSummarySaveYourOrderCheckoutFaster,
              })
            }
            ariaLable={translateWithI18Next(saveOrderButtonLabel)}
          >
            {translateWithI18Next(saveOrderButtonLabel)}
          </ButtonComp>
        </span>
      </div>
    )
  );
};

/*********************************************************************
 * Get create KFC account panel
 * params -
 *********************************************************************/
const createKFCAccountHandler = params => {
  const { orderDetails } = params;
  const { total, currencyConversionFactor, customer } = orderDetails;
  const { firstName, lastName, email, phoneNumber, id } = customer;
  PostOrderSignInClick();
  const getBase64String = data => Buffer.from(data).toString('base64');
  sessionStorage.setItem(SESSION_STORAGE_KEYS.ORDER_TRACKING_CLICK_KFC_ACCOUNT, id);
  const marketingCheckbox = sessionStorage.getItem(SESSION_STORAGE_KEYS.MARKETING_OPT_VAL);
  let accountParams = `userFirstName=${getBase64String(firstName)}&userLastName=${getBase64String(
    lastName,
  )}&userEmail=${getBase64String(email)}&userPhoneNumber=${getBase64String(phoneNumber)}`;
  if (marketingCheckbox) {
    const marketingOptInVal = JSON.parse(marketingCheckbox);
    const { Email: emailSubscription, Phone: phoneSubscription } = marketingOptInVal;
    accountParams = emailSubscription ? `${accountParams}&emailSubscription=${emailSubscription}` : accountParams;
    accountParams = phoneSubscription ? `${accountParams}&phoneSubscription=${phoneSubscription}` : accountParams;
  }
  createAccount(accountParams);
  SignUpClick({ orderValue: calculatePrice(total / currencyConversionFactor) });
};

const getCreateKFCAccountPanelJSX = params => {
  return (
    <div className='savingdivDelivery'>
      <span className='savingText'>{translateWithI18Next(TRANSLATE_MAPPING_KEY?.REORDER_FAVORITES)}</span>
      <span className='saveButton'>
        <ButtonComp
          data-testid='create-kfc-account-btn'
          className='button transparent-btn black-border'
          onClick={() => createKFCAccountHandler(params)}
          ariaLable={translateWithI18Next(TRANSLATE_MAPPING_KEY?.CREATE_KFC_ACCOUNT)}
        >
          {translateWithI18Next(TRANSLATE_MAPPING_KEY?.CREATE_KFC_ACCOUNT)}
        </ButtonComp>
      </span>
    </div>
  );
};

/*********************************************************************
 * Get Save delivery address panel
 * params -
 *********************************************************************/
const getSaveDeliveryAddressPanelJSX = params => {
  const { updateSetOrderDetails } = params;

  return (
    <div className='savingdivDelivery'>
      <span className='savingText'>{translateWithI18Next(TRANSLATE_MAPPING_KEY?.SAVE_ADDRESS_BOOK)}</span>
      <span className='saveButton'>
        <ButtonComp
          data-testid='save-delivery-address'
          className='button transparent-btn black-border'
          onClick={() => updateSetOrderDetails({ isOpenSaveDeliveryAddressModal: true })}
          ariaLable={translateWithI18Next(TRANSLATE_MAPPING_KEY?.SAVE_DELIVERY_ADDRESS)}
        >
          {translateWithI18Next(TRANSLATE_MAPPING_KEY?.SAVE_DELIVERY_ADDRESS)}
        </ButtonComp>
      </span>
    </div>
  );
};

/*********************************************************************
 * Get Cancel order Help Container
 * params -
 *********************************************************************/
const getCancelOrderHelpContainerJSX = params => {
  const { orderDetails } = params;
  const { helpContantData } = orderDetails;
  const { helpSectionCallDetails } = helpContantData;

  return (
    <div className='savingdivDelivery'>
      <span className='savingText'>{translateWithI18Next(TRANSLATE_MAPPING_KEY?.HERE_TO_HELP)}</span>
      <span className='saveButton'>
        <a tabIndex='-1' href={'tel:' + helpSectionCallDetails?.contactNo} className='anchorTags'>
          <ButtonComp
            ariaLable='contact us'
            data-testid='contact-us-btn'
            className='button transparent-btn black-border'
          >
            <ImageComponent srcFile={helpSectionCallDetails?.icon} alt=' ' className='Messageimage' />
            {`${helpSectionCallDetails?.buttonText} ${helpSectionCallDetails?.contactNo}`}
          </ButtonComp>
        </a>
      </span>
    </div>
  );
};

/*********************************************************************
 * Get Save your Orders & Create KFC Account Container
 * params -
 *********************************************************************/
export const getBottomPanelTemplate = params => {
  const { orderDetails } = params;
  const { orderSummarySaveYourOrderCompleted, userStatus, savedAddressSucess, saveFavoriteSummarySucess } =
    orderDetails;

  const getPanel = () => {
    if (isDeliveryOrderCancelledOrRefund(orderDetails) || isPickupOrderCancelled(orderDetails)) {
      return getCancelOrderHelpContainerJSX(params);
    } else if (orderSummarySaveYourOrderCompleted && orderDetails?.deliveryAddress && !savedAddressSucess) {
      return getSaveDeliveryAddressPanelJSX(params);
    } else if (userStatus?.loggedIn && !savedAddressSucess && !saveFavoriteSummarySucess) {
      return getSaveYourOrdersAndCheckoutFasterJSX(params);
    } else if (savedAddressSucess || saveFavoriteSummarySucess) {
      return;
    }
    return getCreateKFCAccountPanelJSX(params);
  };

  return <div className='saveYourOrdersAndCreateKFCAccountContainer col-12'>{getPanel()}</div>;
};
